import React, { useState, useEffect } from "react";
import {
  FaChartLine,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaSync,
  FaDatabase,
  FaClipboardCheck,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSpinner,
  FaFileExport,
  FaBalanceScale,
  FaChartBar,
  FaTable,
} from "react-icons/fa";
import axios from "axios";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "./SoloPaymentStatistics.css";

const SoloPaymentStatistics = ({
  studentData,
  onRefresh,
  isLoading = false,
  initialData = null,
}) => {
  const [statsData, setStatsData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(isLoading);
  const [error, setError] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [timePeriod, setTimePeriod] = useState("year");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeChart, setActiveChart] = useState("bar"); // 'bar', 'line', 'pie'
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    if (studentData?.studentId) {
      fetchStatisticsData();
    }
  }, [studentData, year, timePeriod]);

  useEffect(() => {
    if (initialData) {
      processInitialData(initialData);
    }
  }, [initialData]);

  const processInitialData = (data) => {
    setStatsData(data);
    if (data.chart_data && Array.isArray(data.chart_data)) {
      const processedChartData = data.chart_data.map((item, index) => ({
        id: index,
        month: item.month_name.substring(0, 3),
        fullMonth: item.month_name,
        monthNumber: item.month,
        feeAmount: parseFloat(item.fee_amount) || 0,
        expenseAmount: parseFloat(item.expense_amount) || 0,
        feePaid: parseFloat(item.fee_paid) || 0,
        expensePaid: parseFloat(item.expense_paid) || 0,
        feePending: parseFloat(item.fee_pending) || 0,
        expensePending: parseFloat(item.expense_pending) || 0,
        totalAmount:
          (parseFloat(item.fee_amount) || 0) +
          (parseFloat(item.expense_amount) || 0),
        totalPaid:
          (parseFloat(item.fee_paid) || 0) +
          (parseFloat(item.expense_paid) || 0),
        totalPending:
          (parseFloat(item.fee_pending) || 0) +
          (parseFloat(item.expense_pending) || 0),
      }));
      setChartData(processedChartData);

      // Set current month as selected by default
      const currentMonth = new Date().getMonth() + 1;
      const currentMonthData = processedChartData.find(
        (item) =>
          item.monthNumber &&
          item.monthNumber.endsWith(
            `-${currentMonth.toString().padStart(2, "0")}`
          )
      );
      if (currentMonthData) {
        setSelectedMonth(currentMonthData);
      }
    }
    setLastUpdated(new Date());
  };

  const fetchStatisticsData = async () => {
    if (!studentData?.studentId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=get-statistics-data",
        {
          student_id: studentData.studentId,
          year: year,
          time_period: timePeriod,
        },
        {
          timeout: 10000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Statistics API Response:", response.data);

      if (response.data.success) {
        processInitialData(response.data);
      } else {
        setError(response.data.error || "Failed to fetch statistics");
      }
    } catch (error) {
      console.error("❌ Failed to fetch statistics:", error);

      if (error.code === "ECONNABORTED") {
        setError("Request timeout. Server is taking too long to respond.");
      } else if (error.response) {
        setError(
          error.response.data?.error || `Server error: ${error.response.status}`
        );
      } else if (error.request) {
        setError("No response from server. Please check your connection.");
      } else {
        setError(error.message || "Failed to fetch statistics");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchStatisticsData();
    if (onRefresh) onRefresh();
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    if (isNaN(num)) return "Rs. 0.00";

    return `Rs. ${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const calculatePercentage = (part, total) => {
    if (!total || total === 0) return 0;
    const percentage = (part / total) * 100;
    return percentage.toFixed(1);
  };

  // Custom Tooltip Component for Charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p
              key={index}
              style={{
                color: entry.color,
                margin: "3px 0",
                display: "flex",
                justifyContent: "space-between",
                gap: "15px",
              }}
            >
              <span>{entry.name}:</span>
              <span style={{ fontWeight: "bold" }}>
                {formatCurrency(entry.value)}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Render Bar Chart
  const renderBarChart = () => {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          onClick={(data) => {
            if (data && data.activePayload && data.activePayload[0]) {
              setSelectedMonth(data.activePayload[0].payload);
            }
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="month"
            angle={-45}
            textAnchor="end"
            height={60}
            tick={{ fontSize: 12, fill: "#666" }}
            stroke="#666"
          />
          <YAxis
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
            tick={{ fontSize: 12, fill: "#666" }}
            stroke="#666"
            label={{
              value: "Amount (Rs.)",
              angle: -90,
              position: "insideLeft",
              offset: -10,
              style: { fill: "#666", fontSize: 12 },
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ paddingTop: "10px" }} verticalAlign="top" />
          <Bar
            name="Fee Amount"
            dataKey="feeAmount"
            fill="#8884d8"
            radius={[4, 4, 0, 0]}
            opacity={0.8}
          />
          <Bar
            name="Expense Amount"
            dataKey="expenseAmount"
            fill="#82ca9d"
            radius={[4, 4, 0, 0]}
            opacity={0.8}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // Render Line Chart
  const renderLineChart = () => {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          onClick={(data) => {
            if (data && data.activePayload && data.activePayload[0]) {
              setSelectedMonth(data.activePayload[0].payload);
            }
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="month"
            angle={-45}
            textAnchor="end"
            height={60}
            tick={{ fontSize: 12, fill: "#666" }}
            stroke="#666"
          />
          <YAxis
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
            tick={{ fontSize: 12, fill: "#666" }}
            stroke="#666"
            label={{
              value: "Amount (Rs.)",
              angle: -90,
              position: "insideLeft",
              offset: -10,
              style: { fill: "#666", fontSize: 12 },
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ paddingTop: "10px" }} verticalAlign="top" />
          <Line
            name="Total Amount"
            type="monotone"
            dataKey="totalAmount"
            stroke="#ff7300"
            strokeWidth={2}
            dot={{ r: 4, strokeWidth: 2 }}
            activeDot={{ r: 6, strokeWidth: 2 }}
          />
          <Line
            name="Fee Paid"
            type="monotone"
            dataKey="feePaid"
            stroke="#8884d8"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ r: 4, strokeWidth: 2 }}
          />
          <Line
            name="Expense Paid"
            type="monotone"
            dataKey="expensePaid"
            stroke="#82ca9d"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ r: 4, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  // Render Pie Chart for Payment Distribution
  const renderPieChart = () => {
    const pieData = [
      {
        name: "Fee Paid",
        value: statsData?.summary?.total_paid_fee || 0,
        color: "#8884d8",
      },
      {
        name: "Expense Paid",
        value: statsData?.summary?.total_paid_expense || 0,
        color: "#82ca9d",
      },
      {
        name: "Fee Pending",
        value: statsData?.summary?.total_pending_fee || 0,
        color: "#ffc658",
      },
      {
        name: "Expense Pending",
        value: statsData?.summary?.total_pending_expense || 0,
        color: "#ff8042",
      },
    ].filter((item) => item.value > 0);

    return (
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) =>
              `${name}: ${(percent * 100).toFixed(1)}%`
            }
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
            onClick={(data) => {
              if (data) {
                setSelectedMonth(data);
              }
            }}
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => formatCurrency(value)} />
          <Legend
            layout="vertical"
            verticalAlign="middle"
            align="right"
            wrapperStyle={{ paddingLeft: "20px" }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  // Render Selected Month Details
  const renderSelectedMonthDetails = () => {
    if (!selectedMonth) return null;

    return (
      <div className="month-details-card">
        <h4>
          <FaCalendarAlt /> {selectedMonth.fullMonth} Details
        </h4>
        <div className="month-details-grid">
          <div className="detail-item">
            <span className="detail-label">Fee Amount:</span>
            <span className="detail-value">
              {formatCurrency(selectedMonth.feeAmount)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Fee Paid:</span>
            <span className="detail-value paid">
              {formatCurrency(selectedMonth.feePaid)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Fee Pending:</span>
            <span className="detail-value pending">
              {formatCurrency(selectedMonth.feePending)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Expense Amount:</span>
            <span className="detail-value">
              {formatCurrency(selectedMonth.expenseAmount)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Expense Paid:</span>
            <span className="detail-value paid">
              {formatCurrency(selectedMonth.expensePaid)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Expense Pending:</span>
            <span className="detail-value pending">
              {formatCurrency(selectedMonth.expensePending)}
            </span>
          </div>
          <div className="detail-item total">
            <span className="detail-label">Total Amount:</span>
            <span className="detail-value">
              {formatCurrency(selectedMonth.totalAmount)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Payment Status:</span>
            <span
              className={`status-badge ${
                selectedMonth.totalPending === 0
                  ? "paid"
                  : selectedMonth.totalPaid > 0
                  ? "partial"
                  : "unpaid"
              }`}
            >
              {selectedMonth.totalPending === 0
                ? "Paid"
                : selectedMonth.totalPaid > 0
                ? "Partial"
                : "Unpaid"}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const handleExportReport = () => {
    if (!statsData) return;

    const dataStr = JSON.stringify(statsData, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const exportFileDefaultName = `statistics_${studentData.studentId}_${year}.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();
  };

  if (loading) {
    return (
      <div className="solo-payment-statistics">
        <div className="statistics-loading">
          <FaSpinner className="loading-spinner" />
          <p>Loading statistics...</p>
          <p className="loading-subtext">
            Fetching data for {studentData?.studentName || "student"}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="solo-payment-statistics">
        <div className="statistics-error">
          <FaExclamationTriangle className="error-icon" />
          <h3>Error Loading Statistics</h3>
          <p>{error}</p>
          <button className="refresh-btn" onClick={handleRefresh}>
            <FaSync /> Retry
          </button>
        </div>
      </div>
    );
  }

  if (!studentData?.studentId) {
    return (
      <div className="solo-payment-statistics">
        <div className="statistics-empty">
          <FaChartLine className="empty-icon" />
          <h3>No Student Selected</h3>
          <p>Please search for a student to view statistics</p>
        </div>
      </div>
    );
  }

  if (!statsData) {
    return (
      <div className="solo-payment-statistics">
        <div className="statistics-empty">
          <FaExclamationTriangle className="empty-icon" />
          <h3>No Statistics Data</h3>
          <p>No statistics data available for this student</p>
          <button className="refresh-btn" onClick={handleRefresh}>
            <FaSync /> Refresh Data
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="solo-payment-statistics">
      {/* Header */}
      <div className="statistics-header">
        <div className="header-left">
          <h2>
            <FaChartLine className="header-icon" />
            Payment Statistics
          </h2>
          <p className="student-info">
            {studentData.studentName} • {studentData.studentClass} •{" "}
            {studentData.studentId}
          </p>
        </div>
        <div className="header-right">
          <div className="period-selector">
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="year-select"
            >
              {[2022, 2023, 2024, 2025, 2026].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <select
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value)}
              className="period-select"
            >
              <option value="year">Yearly</option>
              <option value="quarter">Quarterly</option>
              <option value="month">Monthly</option>
            </select>
          </div>
          <button
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? <FaSpinner className="spin" /> : <FaSync />}
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card total-card">
          <div className="card-icon">
            <FaMoneyBillWave />
          </div>
          <div className="card-content">
            <h3>Total Amount</h3>
            <div className="card-value">
              {formatCurrency(statsData?.summary?.total_amount || 0)}
            </div>
            <div className="card-subtext">All transactions in {year}</div>
          </div>
        </div>

        <div className="summary-card paid-card">
          <div className="card-icon">
            <FaCheckCircle />
          </div>
          <div className="card-content">
            <h3>Amount Paid</h3>
            <div className="card-value">
              {formatCurrency(statsData?.summary?.total_paid || 0)}
            </div>
            <div className="card-subtext">
              {calculatePercentage(
                statsData?.summary?.total_paid || 0,
                statsData?.summary?.total_amount || 1
              )}
              % of total
            </div>
          </div>
        </div>

        <div className="summary-card pending-card">
          <div className="card-icon">
            <FaExclamationTriangle />
          </div>
          <div className="card-content">
            <h3>Amount Pending</h3>
            <div className="card-value">
              {formatCurrency(statsData?.summary?.total_pending || 0)}
            </div>
            <div className="card-subtext">Requires attention</div>
          </div>
        </div>

        <div className="summary-card balance-card">
          <div className="card-icon">
            <FaBalanceScale />
          </div>
          <div className="card-content">
            <h3>Fee vs Expense</h3>
            <div className="balance-breakdown">
              <div className="balance-item">
                <span className="balance-label">Fee:</span>
                <span className="balance-value">
                  {formatCurrency(statsData?.summary?.total_fee || 0)}
                  <span className="balance-percentage">
                    ({statsData?.summary?.fee_percentage || 0}%)
                  </span>
                </span>
              </div>
              <div className="balance-item">
                <span className="balance-label">Expense:</span>
                <span className="balance-value">
                  {formatCurrency(statsData?.summary?.total_expense || 0)}
                  <span className="balance-percentage">
                    ({statsData?.summary?.expense_percentage || 0}%)
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      {chartData.length > 0 && (
        <div className="chart-section">
          <div className="chart-header">
            <h3>
              <FaChartBar /> Payment Analytics ({year})
            </h3>
            <div className="chart-controls">
              <div className="chart-type-selector">
                <button
                  className={`chart-type-btn ${
                    activeChart === "bar" ? "active" : ""
                  }`}
                  onClick={() => setActiveChart("bar")}
                >
                  Bar Chart
                </button>
                <button
                  className={`chart-type-btn ${
                    activeChart === "line" ? "active" : ""
                  }`}
                  onClick={() => setActiveChart("line")}
                >
                  Line Chart
                </button>
                <button
                  className={`chart-type-btn ${
                    activeChart === "pie" ? "active" : ""
                  }`}
                  onClick={() => setActiveChart("pie")}
                >
                  Pie Chart
                </button>
              </div>
            </div>
          </div>

          <div className="chart-container">
            {activeChart === "bar" && renderBarChart()}
            {activeChart === "line" && renderLineChart()}
            {activeChart === "pie" && renderPieChart()}
          </div>

          {renderSelectedMonthDetails()}
        </div>
      )}

      {/* Data Table */}
      {chartData.length > 0 && (
        <div className="data-table-section">
          <h3>
            <FaTable /> Monthly Data Table
          </h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Fee Amount</th>
                  <th>Fee Paid</th>
                  <th>Fee Pending</th>
                  <th>Expense Amount</th>
                  <th>Expense Paid</th>
                  <th>Expense Pending</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item) => (
                  <tr
                    key={item.id}
                    className={selectedMonth?.id === item.id ? "selected" : ""}
                    onClick={() => setSelectedMonth(item)}
                  >
                    <td>{item.fullMonth}</td>
                    <td>{formatCurrency(item.feeAmount)}</td>
                    <td className="paid">{formatCurrency(item.feePaid)}</td>
                    <td className="pending">
                      {formatCurrency(item.feePending)}
                    </td>
                    <td>{formatCurrency(item.expenseAmount)}</td>
                    <td className="paid">{formatCurrency(item.expensePaid)}</td>
                    <td className="pending">
                      {formatCurrency(item.expensePending)}
                    </td>
                    <td>{formatCurrency(item.totalAmount)}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          item.totalPending === 0
                            ? "paid"
                            : item.totalPaid > 0
                            ? "partial"
                            : "unpaid"
                        }`}
                      >
                        {item.totalPending === 0
                          ? "Paid"
                          : item.totalPaid > 0
                          ? "Partial"
                          : "Unpaid"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <strong>Totals</strong>
                  </td>
                  <td>
                    <strong>
                      {formatCurrency(
                        chartData.reduce((sum, item) => sum + item.feeAmount, 0)
                      )}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {formatCurrency(
                        chartData.reduce((sum, item) => sum + item.feePaid, 0)
                      )}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {formatCurrency(
                        chartData.reduce(
                          (sum, item) => sum + item.feePending,
                          0
                        )
                      )}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {formatCurrency(
                        chartData.reduce(
                          (sum, item) => sum + item.expenseAmount,
                          0
                        )
                      )}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {formatCurrency(
                        chartData.reduce(
                          (sum, item) => sum + item.expensePaid,
                          0
                        )
                      )}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {formatCurrency(
                        chartData.reduce(
                          (sum, item) => sum + item.expensePending,
                          0
                        )
                      )}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {formatCurrency(
                        chartData.reduce(
                          (sum, item) => sum + item.totalAmount,
                          0
                        )
                      )}
                    </strong>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Summary Section */}
      {statsData?.summary && (
        <div className="detailed-summary">
          <h3>
            <FaClipboardCheck /> Detailed Summary
          </h3>
          <div className="summary-grid">
            <div className="summary-item">
              <div className="summary-label">Total Transactions</div>
              <div className="summary-value">
                {(statsData.summary.fee_transactions || 0) +
                  (statsData.summary.expense_transactions || 0)}
              </div>
              <div className="summary-breakdown">
                <span className="breakdown-item fee">
                  Fee: {statsData.summary.fee_transactions || 0}
                </span>
                <span className="breakdown-item expense">
                  Expense: {statsData.summary.expense_transactions || 0}
                </span>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Payment Completion</div>
              <div className="summary-value">
                {calculatePercentage(
                  statsData.summary.total_paid || 0,
                  statsData.summary.total_amount || 1
                )}
                %
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${calculatePercentage(
                      statsData.summary.total_paid || 0,
                      statsData.summary.total_amount || 1
                    )}%`,
                  }}
                ></div>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Average Monthly</div>
              <div className="summary-value">
                {formatCurrency((statsData.summary.total_amount || 0) / 12)}
              </div>
              <div className="summary-subtext">Per month average</div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Pending Ratio</div>
              <div className="summary-value">
                {calculatePercentage(
                  statsData.summary.total_pending || 0,
                  statsData.summary.total_amount || 1
                )}
                %
              </div>
              <div className="summary-subtext">Of total amount</div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="statistics-footer">
        <div className="footer-info">
          <FaDatabase className="footer-icon" />
          <span>
            Last updated:{" "}
            {lastUpdated
              ? lastUpdated.toLocaleString()
              : new Date().toLocaleString()}
          </span>
          {statsData?.summary && (
            <span className="data-count">
              • {chartData.length} months • {statsData.summary.fee_transactions}{" "}
              fee • {statsData.summary.expense_transactions} expense
            </span>
          )}
        </div>
        <div className="footer-actions">
          <button
            className="export-btn"
            onClick={handleExportReport}
            disabled={!statsData}
          >
            <FaFileExport /> Export Report
          </button>
          <button className="print-btn" onClick={() => window.print()}>
            Print Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoloPaymentStatistics;
